import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { camelizeKeys } from 'humps';
import PropTypes from 'prop-types';
import { Button, DialogMessage, Text, Alert } from '@reservamos/elements';
import * as types from 'constants/ActionTypes';
import { setError } from '@/actions';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { executeRecaptcha } from 'utils/googleRecaptcha';
import { trackModifyTripAttempt } from 'metrics/user-analytics/exchange';
import { buildExchangeSchedulePath } from 'utils/exchange';
import TicketListBlock from './TicketsListBlock';
import BillingSection from './BillingModal/BillingSection';
import RescheduleSection from './RescheduleModal/RescheduleSection';

/**
 * ExchangeTicketList component.
 *
 * @param {object} props - currentTicket, validateExchange, allPassengers, tripReturn, origin, destination, tripDepart, selectedPassengerToAlter, cancelTicket, getCancelledTicketStatusPoll, purchaseToken, cancelStatus, hasConnections, hasConnectionsReturns, tripDepartConnection, tripReturnConnection, departCancelAllowed, returnCancelAllowed, isPix, rescheduleTicket, validateChangeDocument.
 * @param {string|object} props.currentTicket - The current ticket details.
 * @param {Function} props.validateExchange - Function to validate the ticket exchange.
 * @param {object} props.allPassengers - Object containing all passengers data.
 * @param {object} props.tripReturn - Return trip details.
 * @param {string} props.origin - Origin location identifier.
 * @param {string} props.destination - Destination location identifier.
 * @param {object} props.tripDepart - Departure trip details.
 * @param {object} props.selectedPassengerToAlter - Selected passenger to alter the ticket for.
 * @param {Function} props.cancelTicket - Function to cancel a ticket.
 * @param {Function} props.getCancelledTicketStatusPoll - Function to poll the status of a cancelled ticket.
 * @param {string} props.purchaseToken - Token used for ticket purchase.
 * @param {string} props.cancelStatus - Status of the ticket cancellation process.
 * @param {boolean} props.hasConnections - Indicates if this trip has connections.
 * @param {boolean} props.hasConnectionsReturns - Indicates if return trip has connections.
 * @param {Array} props.tripDepartConnection - Departure trip details when trip has connections.
 * @param {Array} props.tripReturnConnection - Return trip details when trip has connections.
 * @param {boolean} props.departCancelAllowed - Indicates if departure ticket cancellation is allowed.
 * @param {boolean} props.returnCancelAllowed - Indicates if return ticket cancellation is allowed.
 * @param {boolean} props.isPix - Indicates if the payment method is Pix.
 * @param {Function} props.rescheduleTicket - Function to reschedule a ticket.
 * @param {Function} props.validateChangeDocument - Function to validate the change document.
 * @param {Function} props.changeDocument - Function to change the document.
 *
 * @returns {JSX.Element} The rendered ticket exchange component.
 */
const ExchangeTicketList = ({
  currentTicket,
  validateExchange,
  allPassengers,
  tripReturn,
  origin,
  destination,
  tripDepart,
  selectedPassengerToAlter,
  cancelTicket,
  getCancelledTicketStatusPoll,
  purchaseToken,
  cancelStatus,
  hasConnections,
  hasConnectionsReturns,
  tripDepartConnection,
  tripReturnConnection,
  departCancelAllowed,
  returnCancelAllowed,
  isPix,
  rescheduleTicket,
  validateChangeDocument,
  changeDocument,
}) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();
  const env = useWhitelabelEnvs();
  const showCieloPixWarning = features.SHOW_CIELO_PIX_WARNING_ON_EXCHANGE;
  const TERMS_AND_CONDITIONS_LINK =
    env?.exchangeContactData?.termsAndConditionsLink &&
    env.exchangeContactData.termsAndConditionsLink.trim() !== '' ? (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
      <a
        href={env.exchangeContactData.termsAndConditionsLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {t('modal_confirm_reschedule.terminos_link')}
      </a>
    ) : null;
  const customerServiceEmail =
    env?.exchangeContactData?.email?.trim() || 'servicioalcliente@cruzdelsur.com.pe';
  const showDetailedCancelDisclaimer = features.SHOW_CANCEL_CONFIRMATION_DISCLAIMER;

  // Use different button text when SearchWidget is enabled
  const exchangeButtonText = features.USE_SEARCH_WIDGET_ON_EXCHANGE
    ? t('ticket_list.button_modify_trip')
    : t('ticket_list.button_change');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isExchangeLoading, setIsExchangeLoading] = useState(false);

  const allowCompleteCancellation = features.ALLOW_COMPLETE_CANCELLATION;
  const showCancelButton = features.SHOW_CANCEL_TICKET_BUTTON;
  const showExchangeButton = features.SHOW_EXCHANGE_TICKET_BUTTON && !allowCompleteCancellation;
  const useSearchWidgetOnExchange = features.USE_SEARCH_WIDGET_ON_EXCHANGE;
  const showEnhancedTicketData = features.SHOW_ENHANCED_TICKET_DATA;
  const validateExchangeEligibility = features.EXCHANGE_VALIDATE_ELIGIBILITY_BEFORE_SCHEDULE;

  const history = useHistory();
  const { operationNumbers, email, document } = useParams();

  const dispatch = useDispatch();
  const usingRecaptcha = features.EXCHANGE_REQUIRE_RECAPTCHA && env.recaptcha;
  const selectedPassengerKey = selectedPassengerToAlter?.ticketKey || '';

  const validateExchangeLocal = useCallback(() => {
    /**
     * Function to handle the recaptcha token
     */
    const onCaptchaVerify = (onRecaptchaToken) => {
      const action = features.EXCHANGE_RECAPTCHA_ACTION;
      executeRecaptcha(action, (recaptchaToken) => {
        onRecaptchaToken(recaptchaToken);
      });
    };

    if (currentTicket === '' || cancelStatus === 'success') {
      dispatch({ type: types.STATUS_CANCEL_RESET });
      const operationNumbersArray = operationNumbers.split(',');

      /**
       * Function to validate the exchange wrapping the recaptcha token if needed
       * @param {*} [recaptchaToken] - Recaptcha token
       * @returns {function} - Dispatch function
       */
      const validationFunction = (recaptchaToken) =>
        validateExchange(
          history,
          operationNumbersArray,
          '',
          '',
          '',
          recaptchaToken,
          email,
          document,
        );

      if (usingRecaptcha) {
        onCaptchaVerify(validationFunction);
      } else {
        validationFunction();
      }
    }
  }, [
    currentTicket,
    cancelStatus,
    dispatch,
    features.EXCHANGE_RECAPTCHA_ACTION,
    operationNumbers,
    validateExchange,
    history,
    email,
    document,
    usingRecaptcha,
  ]);

  /**
   * getOnClickedTicket Function
   *
   * Returns a function that handles clicking on a specific ticket.
   *
   * @param {string} nodeTripName - The name of the trip node (e.g., depart or return).
   * @returns {Function} A function that takes a ticketKey and performs an action on the clicked ticket.
   *
   */
  const getOnClickedTicket = (nodeTripName) => (ticketKey) => {
    dispatch({
      type: types.EXCHANGE_SELECTED_PASSENGER_TO_ALTER,
      selectedPassengerToAlter: {
        ticketKey,
        nodeTripName,
      },
    });
  };

  /**
   * navigateToExchangeSchedule Function
   *
   * Navigates to the exchange schedule page with the selected passengers.
   * Updates Redux state with selected passengers and trip information.
   *
   * @param {string} ticketKey - The ticket key to exchange
   * @param {string} nodeTripName - The trip node name ('depart' or 'return')
   * @returns {void}
   */
  const navigateToExchangeSchedule = (ticketKey, nodeTripName) => {
    const allPassengersGrouped = [...allPassengers.depart, ...allPassengers.return];
    const nodeTripNameValue = nodeTripName === 'depart' ? tripDepart : tripReturn;

    const selectedPassengers = allPassengersGrouped.filter((passenger) => {
      return passenger.transporterKey === ticketKey;
    });

    dispatch({
      type: types.EXCHANGE_UPDATE_PASSENGERS,
      passengers: selectedPassengers,
      trip: nodeTripNameValue,
    });

    // Only use trip-based path building for SearchWidget flow (handles round-trip origin/destination swap)
    const schedulePath = useSearchWidgetOnExchange
      ? buildExchangeSchedulePath(operationNumbers, nodeTripNameValue, origin, destination)
      : `/exchange/schedule/${operationNumbers}/${origin}/${destination}`;

    history.push(schedulePath);
  };

  /**
   * Validates if a ticket is eligible for exchange by calling the rescheduleTicket API.
   * Controlled by feature flag: EXCHANGE_VALIDATE_ELIGIBILITY_BEFORE_SCHEDULE
   *
   * @param {object} ticket - The ticket object to validate
   * @param {boolean} skipValidation - If true, skip validation and return success
   * @returns {Promise<{success: boolean, message?: string}>} Validation result
   */
  const validateTicketEligibilityForExchange = async (ticket, skipValidation = false) => {
    if (!validateExchangeEligibility || skipValidation) {
      return {
        success: true,
        message: '',
      };
    }

    const {
      companyCode,
      documentType,
      documentPrefix,
      documentNumber,
      email: ticketEmail,
    } = ticket;

    try {
      const rescheduleResponse = await rescheduleTicket({
        companyCode,
        documentType,
        documentPrefix,
        documentNumber,
        email: ticketEmail ?? email,
        reserveCode: operationNumbers,
      });

      return {
        success: rescheduleResponse.status,
        message: rescheduleResponse.message,
      };
    } catch (error) {
      return {
        success: false,
        message: t('ticket_list.exchange_validation_error'),
      };
    }
  };

  /**
   * Handles the process of exchanging the selected tickets.
   * For CDS V2: Validates ticket eligibility before navigation if feature flag is enabled.
   *
   * @returns {Promise<void>}
   */
  const handleExchangeSelectedTickets = async () => {
    if (selectedPassengerToAlter.ticketKey === '') {
      dispatch(
        setError(200, 'select_a_ticket', 'warning', false, t('ticket_list.select_a_ticket')),
      );
      return;
    }

    const { ticketKey, nodeTripName } = selectedPassengerToAlter;
    const group = allPassengers[nodeTripName];
    const ticket = group?.find(({ transporterKey }) => transporterKey === ticketKey);

    if (!ticket) {
      dispatch(
        setError(200, 'ticket_not_found', 'warning', false, t('ticket_list.ticket_not_found')),
      );
      return;
    }

    // Skip validate_reschedule when modifying already rescheduled tickets
    const isModifyOperation = useSearchWidgetOnExchange;

    if (validateExchangeEligibility && !isModifyOperation) {
      setIsExchangeLoading(true);

      const validation = await validateTicketEligibilityForExchange(ticket, false);

      setIsExchangeLoading(false);

      if (!validation.success) {
        const errorMsg = validation.message || t('ticket_list.exchange_validation_failed');
        trackModifyTripAttempt(false, errorMsg);
        dispatch(setError(200, 'exchange_validation_failed', 'warning', false, errorMsg));
        return;
      }
    }

    trackModifyTripAttempt(true);
    navigateToExchangeSchedule(ticketKey, nodeTripName);
  };

  useEffect(() => {
    // Run validateExchangeLocal on mount
    validateExchangeLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateExchangeLocal]);

  /**
   * handleCancelSelectedTickets Function
   *
   * Handles the process of cancelling the selected tickets.
   *
   * This function is called when the user chooses to cancel one ticket or all tickets (if ALLOW_COMPLETE_CANCELLATION is true),
   * and performs the necessary logic to process the cancellation.
   *
   * @returns {void}
   */
  const handleCancelSelectedTickets = () => {
    if (selectedPassengerToAlter.ticketKey !== '' || features.ALLOW_COMPLETE_CANCELLATION) {
      setShowCancelModal(true);
    } else {
      dispatch(
        setError(200, 'select_a_ticket', 'warning', false, t('ticket_list.select_a_ticket')),
      );
    }
  };

  /**
   * getSelectedTicket Function
   *
   * Gets the selected ticket data from allPassengers based on selectedPassengerToAlter.
   *
   * @returns {object|null} The selected ticket object or null if no ticket is selected
   */
  const getSelectedTicket = () => {
    if (!selectedPassengerToAlter?.ticketKey) {
      return null;
    }

    const { ticketKey, nodeTripName } = selectedPassengerToAlter;
    const group = allPassengers[nodeTripName];

    if (!group) {
      return null;
    }

    const selectedTicket = group.find(({ transporterKey }) => transporterKey === ticketKey);

    return selectedTicket || null;
  };

  /**
   * handleCancelModalQuit Function
   *
   * Handles the closing or quitting of the cancel ticket modal.
   *
   * This function is called when the user decides to exit the modal without completing
   * the cancellation process, and it performs the necessary actions to close or clean up the modal state.
   *
   * @returns {void}
   */
  const handleCancelModalQuit = () => {
    setShowCancelModal(false);
  };

  /**
   * handleCancelModalConfirm Function
   *
   * Handles the confirmation of ticket cancellation within the modal.
   *
   * This async function is called when the user confirms the cancellation of selected tickets
   * in the modal. It processes the cancellation and performs any necessary asynchronous operations.
   * If ALLOW_COMPLETE_CANCELLATION is true, it will cancel all tickets.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when the cancellation process is completed.
   */
  const handleCancelModalConfirm = async () => {
    if (selectedPassengerToAlter.ticketKey !== '' || features.ALLOW_COMPLETE_CANCELLATION) {
      // For complete cancellation, backend will handle all tickets
      const ticketKey = features.ALLOW_COMPLETE_CANCELLATION
        ? []
        : [selectedPassengerToAlter.ticketKey];
      const id = await cancelTicket(purchaseToken, ticketKey);
      await getCancelledTicketStatusPoll(purchaseToken, id);
      if (cancelStatus === 'success') {
        validateExchangeLocal();
      }
    }
    setShowCancelModal(false);
  };

  // Tickets that live inside connection legs are NOT in allPassengers.depart/return;
  // they arrive in tripDepartConnection / tripReturnConnection. Include them so the
  // "all processed" check doesn't disable the cancel button while connection legs are
  // still payed (e.g. round-trip with a connection on one direction).
  const connectionTickets = [
    ...(tripDepartConnection || []),
    ...(tripReturnConnection || []),
  ].flatMap((connection) => connection.tickets || []);

  // Check if all tickets are canceled or refund requested
  const allTickets = [
    ...(allPassengers.depart || []),
    ...(allPassengers.return || []),
    ...connectionTickets,
  ];
  const allTicketsCancelledOrRefunded =
    allTickets.length > 0 &&
    allTickets.every((p) => p.state === 'canceled' || p.state === 'refund_requested');

  // Determine if any ticket is selected
  const isTicketSelected = selectedPassengerToAlter?.ticketKey !== '';

  // Determine which trip's cancellation permission to use based on selected ticket
  const selectedTripCancelAllowed =
    selectedPassengerToAlter?.nodeTripName === 'return' ? returnCancelAllowed : departCancelAllowed;

  /**
   * Gets the title text based on feature flags and trip type.
   * @returns {string} The title text to display.
   */
  const getTitleText = () => {
    if (showEnhancedTicketData) {
      return t('ticket_list.title_enhanced');
    }

    return Object.keys(allPassengers.return || {}).length !== 0
      ? t('ticket_list.title_round_trip')
      : t('ticket_list.title_one_way');
  };

  /**
   * Gets the subtitle text based on reschedule mode and number of tickets.
   * @returns {string} The subtitle text to display.
   */
  const getSubtitleText = () => {
    const totalTickets = (allPassengers.depart?.length || 0) + (allPassengers.return?.length || 0);
    const isMultipleTickets = totalTickets > 1;

    if (useSearchWidgetOnExchange) {
      return isMultipleTickets
        ? t('ticket_list.subtitle_reschedule_plural')
        : t('ticket_list.subtitle_reschedule');
    }

    return isMultipleTickets ? t('ticket_list.subtitle_plural') : t('ticket_list.subtitle');
  };

  /**
   * handleTicketRefresh Function
   *
   * Handles refreshing ticket data after successful operations.
   * Marks that selection should be cleared after refresh completes.
   *
   * @returns {void}
   */
  const handleTicketRefresh = () => {
    const operationNumbersArray = operationNumbers.split(',');
    validateExchange(history, operationNumbersArray, '', '', '', '', email, document);

    // TODO: This is a temporary solution to clear the selected ticket after refresh.
    // Clear selected ticket after refresh to ensure clean state
    setTimeout(() => {
      dispatch({
        type: types.EXCHANGE_SELECTED_PASSENGER_TO_ALTER,
        selectedPassengerToAlter: {
          ticketKey: '',
          nodeTripName: '',
        },
      });
    }, 100);
  };

  /**
   * Renders the bottom disclaimer text based on feature flags.
   * @returns {JSX.Element|null} The disclaimer text with container div or null if hidden.
   */
  const renderBottomDisclaimer = () => {
    if (useSearchWidgetOnExchange) {
      return null;
    }

    /**
     * Gets the appropriate disclaimer text based on feature flags.
     * @returns {string} The disclaimer text to display.
     */
    const getDisclaimerText = () => {
      return features.SHOW_BOTTOM_TEXT_EXTENDED
        ? t('ticket_list.bottom_text_extended')
        : t('ticket_list.bottom_text');
    };

    /**
     * Renders the refund pending disclaimer if enabled.
     * @returns {JSX.Element|null} The refund disclaimer element or null if disabled.
     */
    const renderRefundDisclaimer = () => {
      if (!features.SHOW_REFUND_PENDING_DISCLAIMER) {
        return null;
      }

      return (
        <p className="text-xs text-gray-400 mt-2">
          {t('disclaimer.exchange_cancelations.refund_pending')}
        </p>
      );
    };

    return (
      <div>
        <p className="text-xs text-gray-400 pr-8">* {getDisclaimerText()}</p>
        {renderRefundDisclaimer()}
      </div>
    );
  };

  return (
    <>
      <div>
        {/* Use presence of return passengers to determine trip type */}
        <h2 className="text-xl font-bold">{getTitleText()}</h2>
        {!allowCompleteCancellation && <p className="text-gray-600 m-0">{getSubtitleText()}</p>}
      </div>

      {/* Display message if all tickets are cancelled/refunded */}
      {allTicketsCancelledOrRefunded && (
        <Alert alertType="info" className="my-4">
          <Text>{t('ticket_list.all_tickets_processed')}</Text>
        </Alert>
      )}

      {/* Departure Tickets */}
      {Object.keys(allPassengers.depart || {}).length !== 0 && (
        <div className="flex flex-col md:flex-row gap-4">
          <TicketListBlock
            trip={tripDepart}
            passengers={allPassengers.depart}
            getOnClickedTicket={getOnClickedTicket('depart')}
            selectedTicketKey={selectedPassengerKey}
            cancelAllowed={departCancelAllowed}
          >
            {/* Title: Use Round trip only if there is a return trip */}
            {tripReturn ? t('ticket_list.departure_trip') : t('ticket_list.one_way_trip')}
          </TicketListBlock>
        </div>
      )}
      {hasConnections && tripDepartConnection.length !== 0 && (
        <div>
          <p className="mb-2 text-sm">
            <span className="mt-0 block">
              <strong>{t('ticket_list.travel_connection')}</strong>
            </span>
            {t('ticket_list.one_way_trip')}
            <span className="ml-4">
              {tripDepart.originDisplay} - {tripDepart.destinationDisplay}
            </span>
          </p>
          <div className="border-b border-2 rounded-md p-5">
            {tripDepartConnection.map((connection, index) => (
              <div key={connection.origin}>
                <p className="mt-5">
                  <strong>
                    {t('ticket_list.connection')} {index + 1}
                  </strong>
                </p>
                {/* Customize the HTML content here */}
                <TicketListBlock
                  trip={{
                    originDisplay: connection.origin,
                    originSlug: connection.origin_slug,
                    destinationDisplay: connection.destination,
                    destinationSlug: connection.destination_slug,
                    date: connection.search_date,
                    time: connection.time,
                  }}
                  passengers={camelizeKeys(connection.tickets)}
                  getOnClickedTicket={getOnClickedTicket('depart')}
                  selectedTicketKey={selectedPassengerKey}
                  cancelAllowed={departCancelAllowed}
                >
                  {t('ticket_list.one_way_trip')}
                </TicketListBlock>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Return Tickets */}
      {Object.keys(allPassengers.return || {}).length !== 0 && (
        <div className="flex flex-col md:flex-row gap-4 mt-4">
          <TicketListBlock
            trip={tripReturn}
            passengers={allPassengers.return}
            getOnClickedTicket={getOnClickedTicket('return')}
            selectedTicketKey={selectedPassengerKey}
            cancelAllowed={returnCancelAllowed}
          >
            {/* Title: Return trip */}
            {t('ticket_list.ride_back')}
          </TicketListBlock>
        </div>
      )}
      {hasConnectionsReturns && tripReturnConnection.length !== 0 && (
        <div>
          <p className="mb-2 text-sm">
            <span className="mt-0 block">
              <strong>{t('ticket_list.travel_connection')}</strong>
            </span>
            {t('ticket_list.ride_back')}
            <span className="ml-4">
              {tripReturn.originDisplay} - {tripReturn.destinationDisplay}
            </span>
          </p>
          <div className="border-b border-2 rounded-md p-5">
            {tripReturnConnection.map((connection, index) => (
              <div key={connection.origin}>
                <p className="mt-5">
                  <strong>
                    {t('ticket_list.connection')} {index + 1}
                  </strong>
                </p>
                {/* Customize the HTML content here */}
                <TicketListBlock
                  trip={{
                    originDisplay: connection.origin,
                    originSlug: connection.origin_slug,
                    destinationDisplay: connection.destination,
                    destinationSlug: connection.destination_slug,
                    date: connection.search_date,
                    time: connection.time,
                  }}
                  passengers={camelizeKeys(connection.tickets)}
                  getOnClickedTicket={getOnClickedTicket('return')}
                  selectedTicketKey={selectedPassengerKey}
                  cancelAllowed={returnCancelAllowed}
                >
                  {t('ticket_list.ride_back')}
                </TicketListBlock>
              </div>
            ))}
          </div>
        </div>
      )}

      {showCieloPixWarning && isPix && (
        <Alert alertType="warning" padding="S">
          <Text>{t('ticket_list.pix_warning')}</Text>
        </Alert>
      )}

      {/* Layout section for disclaimer text and action buttons */}
      <div className="flex flex-col md:flex-row justify-between items-start gap-4 mt-8">
        <div className="flex-1">{renderBottomDisclaimer()}</div>
        <div className="flex flex-wrap justify-end gap-2 md:gap-4">
          <RescheduleSection
            selectedPassengerToAlter={selectedPassengerToAlter}
            allPassengers={allPassengers}
            rescheduleTicket={rescheduleTicket}
            isTicketSelected={isTicketSelected}
            validateExchange={validateExchange}
            operationNumbers={operationNumbers}
            email={email}
            document={document}
            onRefreshData={handleTicketRefresh}
          />
          <BillingSection
            purchaseToken={purchaseToken}
            validateChangeDocument={validateChangeDocument}
            changeDocument={changeDocument}
            operationNumbers={operationNumbers}
            email={email}
            getSelectedTicket={getSelectedTicket}
            isTicketSelected={isTicketSelected}
            onRefreshData={handleTicketRefresh}
          />
          {/* Show Exchange Button unless HIDE_EXCHANGE_TICKET_BUTTON is true */}
          {showExchangeButton && (
            <Button
              buttonType="submit"
              text={exchangeButtonText}
              variant={useSearchWidgetOnExchange ? 'accent' : 'noFill'}
              isRounded
              withHeight
              fullWidthOnSmall
              className="inline-flex w-auto"
              onClick={handleExchangeSelectedTickets}
              isDisabled={allTicketsCancelledOrRefunded || !isTicketSelected || isExchangeLoading}
              isLoading={isExchangeLoading}
            />
          )}
          {showCancelButton && (
            <Button
              buttonType="submit"
              text={
                allowCompleteCancellation
                  ? t('ticket_list.button_cancel_plural')
                  : t('ticket_list.button_cancel')
              }
              variant="accent"
              isRounded
              withHeight
              fullWidthOnSmall
              className="inline-flex w-auto"
              onClick={handleCancelSelectedTickets}
              isDisabled={
                allTicketsCancelledOrRefunded ||
                (!allowCompleteCancellation && !isTicketSelected) ||
                (!allowCompleteCancellation && !selectedTripCancelAllowed)
              }
            />
          )}
        </div>
      </div>
      {showCancelModal && (
        <DialogMessage
          onClickPrimary={() => handleCancelModalConfirm()}
          onClickSecondary={() => handleCancelModalQuit()}
          primaryButton={t('modal_confirm_cancellation.primary_button')}
          secondaryButton={t('modal_confirm_cancellation.secondary_button')}
          title={
            features.ALLOW_COMPLETE_CANCELLATION
              ? t('modal_confirm_cancellation.title_plural')
              : t('modal_confirm_cancellation.title')
          }
        >
          <Text>{t('modal_confirm_cancellation.first_text')}</Text>
          {showDetailedCancelDisclaimer && (
            <ul className="list-disc pl-5">
              <li>
                <Text>{t('modal_confirm_cancellation.detailed_second_text')}</Text>
              </li>
              <li>
                <Text>
                  <Trans t={t} i18nKey="modal_confirm_cancellation.detailed_third_text" as="p">
                    {TERMS_AND_CONDITIONS_LINK}
                  </Trans>
                </Text>
                <ul className="list-disc pl-5">
                  <li>
                    <Text>
                      <Trans t={t} i18nKey="modal_confirm_cancellation.detailed_fourth_text">
                        <strong>Devolución automática</strong>
                      </Trans>
                    </Text>
                  </li>
                  <li>
                    <Text>
                      <Trans t={t} i18nKey="modal_confirm_cancellation.detailed_fifth_text">
                        <a href={`mailto:${customerServiceEmail}`} className="underline">
                          {customerServiceEmail}
                        </a>
                      </Trans>
                    </Text>
                  </li>
                </ul>
              </li>
            </ul>
          )}
          {!showDetailedCancelDisclaimer && features.SHOW_EXCHANGE_DISCLAIMER && (
            <Text>{t('modal_confirm_cancellation.second_text')}</Text>
          )}
        </DialogMessage>
      )}
    </>
  );
};

ExchangeTicketList.propTypes = {
  currentTicket: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  validateExchange: PropTypes.func,
  allPassengers: PropTypes.object,
  tripReturn: PropTypes.object,
  origin: PropTypes.string,
  destination: PropTypes.string,
  tripDepart: PropTypes.object,
  selectedPassengerToAlter: PropTypes.object,
  cancelTicket: PropTypes.func,
  getCancelledTicketStatusPoll: PropTypes.func,
  purchaseToken: PropTypes.string,
  cancelStatus: PropTypes.string,
  hasConnections: PropTypes.bool,
  hasConnectionsReturns: PropTypes.bool,
  tripDepartConnection: PropTypes.array,
  tripReturnConnection: PropTypes.array,
  departCancelAllowed: PropTypes.bool,
  returnCancelAllowed: PropTypes.bool,
  isPix: PropTypes.bool,
  rescheduleTicket: PropTypes.func,
  validateChangeDocument: PropTypes.func,
  changeDocument: PropTypes.func,
};

export default ExchangeTicketList;
